import { alpha, type Components, type Theme } from '@mui/material/styles';
import { brand, brandDark } from './tokens';

function getLayoutOverrides(isDark: boolean): Components<Omit<Theme, 'components'>> {
  return {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: isDark ? `${brandDark.scrollThumb} ${brand.ink}` : undefined,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', // Remove MUI's default gradient in dark mode
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          boxShadow: isDark
            ? '0 2px 12px rgba(0,0,0,0.4)'
            : '0 2px 12px rgba(21, 33, 27, 0.06)',
          border: isDark
            ? `1px solid ${brandDark.line}`
            : `1px solid ${brand.line}`,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: isDark
              ? '0 4px 20px rgba(0,0,0,0.5)'
              : '0 4px 20px rgba(21, 33, 27, 0.10)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: brand.ink, // dark ink header in both modes, matching the design
          color: brand.paper,
          boxShadow: isDark
            ? '0 1px 0 rgba(238,242,232,0.12)'
            : '0 2px 8px rgba(21, 33, 27, 0.15)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        // Temporary drawers float over content, so they take the raised surface. Set on the paper slot at the
        // same specificity as a drawer's own PaperProps sx, which still wins.
        paper: {
          ...(isDark && {
            backgroundColor: brand.inkSoft,
            variants: [{ props: { variant: 'temporary' }, style: { backgroundColor: brandDark.raised } }],
          }),
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: isDark ? brandDark.line : undefined,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: isDark ? brandDark.raised : brand.card,
          borderRadius: '16px',
          boxShadow: isDark
            ? '0 12px 32px rgba(0,0,0,0.6)'
            : '0 12px 32px rgba(21, 33, 27, 0.12)',
        },
      },
    },
  };
}

function getButtonOverrides(isDark: boolean): Components<Omit<Theme, 'components'>> {
  return {
    MuiIconButton: {
      styleOverrides: {
        root: {
          // Enforce 44x44px touch target on coarse/touch pointers without blowing up layout geometry
          '@media (pointer: coarse)': {
            minWidth: 44,
            minHeight: 44,
          },
          // Exempt icon buttons inside input fields to preserve input height and alignment
          '.MuiInputAdornment-root &': {
            minWidth: 'auto',
            minHeight: 'auto',
          },
          '.MuiAutocomplete-endAdornment &': {
            minWidth: 'auto',
            minHeight: 'auto',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '100px', // pill buttons, matching the site design
          fontWeight: 600,
          padding: '10px 20px',
          fontSize: '0.9375rem', // 15px — matches the design's nav/button scale
          lineHeight: 1.4,
          transition: 'all 0.2s ease-in-out',
          // The disabled fill shows the state; the label stays legible on it (3:1, like text.disabled).
          // action.disabled (0.26 / 0.3) is right for icons and borders but too faint for words.
          '&.Mui-disabled': {
            color: isDark ? alpha(brand.paper, 0.5) : alpha(brand.ink, 0.55),
          },
        },
        sizeSmall: { fontSize: '0.8125rem', padding: '6px 14px' },
        sizeLarge: { fontSize: '1rem', padding: '12px 26px' },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: isDark ? '0 10px 24px -10px rgba(0,0,0,0.6)' : '0 10px 24px -12px rgba(21,33,27,0.45)',
            transform: 'translateY(-2px)',
          },
        },
        containedPrimary: {
          backgroundColor: isDark ? brandDark.accent : brand.accent,
          color: isDark ? brandDark.onAccent : '#FFFFFF',
          '&:hover': {
            // Dark goes lighter on hover, light goes deeper.
            backgroundColor: isDark ? brandDark.accentLight : brand.accentDeep,
          },
        },
        // MUI fills inherit-contained buttons with grey[800], which the inverted dark grey scale makes near-white.
        containedInherit: {
          backgroundColor: isDark ? brandDark.top : undefined,
          color: isDark ? brand.paper : undefined,
          '&:hover': {
            backgroundColor: isDark ? '#3e5245' : undefined,
          },
        },
        containedSecondary: {
          backgroundColor: isDark ? brand.paper : brand.ink,
          color: isDark ? brand.ink : brand.paper,
          '&:hover': {
            backgroundColor: isDark ? '#FFFFFF' : '#0e1813',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          borderColor: isDark ? brandDark.outline : brand.line,
          color: isDark ? brand.paper : brand.ink,
          '&:hover': {
            borderWidth: '1.5px',
            borderColor: isDark ? brand.paper : brand.ink,
            backgroundColor: isDark ? 'rgba(238,242,232,0.08)' : 'transparent',
            transform: 'translateY(-2px)',
          },
        },
        text: {
          color: isDark ? brand.paper : brand.ink,
          fontWeight: 500, // nav-link style buttons are lighter than CTAs
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color: isDark ? brandDark.text2 : undefined,
          borderColor: isDark ? brandDark.outline : undefined,
          '&.Mui-selected': {
            color: isDark ? brand.paper : undefined,
            backgroundColor: isDark ? alpha(brandDark.accent, 0.2) : alpha(brand.accent, 0.14),
          },
        },
      },
    },
  };
}

function getInputOverrides(isDark: boolean): Components<Omit<Theme, 'components'>> {
  return {
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? brand.paper : brand.ink,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? brandDark.accent : brand.accent,
              borderWidth: '2px',
            },
          },
          '& .MuiInputLabel-root': {
            color: isDark ? brandDark.text2 : undefined,
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: isDark ? brandDark.accent : undefined,
          },
          '& .MuiInputLabel-root.Mui-error': {
            color: isDark ? brandDark.error : undefined,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: isDark ? brandDark.outline : undefined,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: isDark ? brand.paper : undefined,
          '& input::placeholder, & textarea::placeholder': {
            color: isDark ? brandDark.text2 : undefined,
            opacity: isDark ? 1 : undefined,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: isDark ? brandDark.raised : undefined,
          border: isDark ? `1px solid ${brandDark.line}` : undefined,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          // MUI's dark thumb is grey[300], which the inverted dark grey scale turns near-black.
          color: isDark ? brand.paper : undefined,
          '&.Mui-checked + .MuiSwitch-track': {
            opacity: isDark ? 0.8 : undefined,
          },
        },
        track: {
          backgroundColor: isDark ? brandDark.text2 : undefined,
          opacity: isDark ? 0.6 : undefined,
        },
      },
    },
  };
}

function getNavigationOverrides(isDark: boolean): Components<Omit<Theme, 'components'>> {
  return {
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.9375rem', // keep dropdown menu items in scale with nav links
          // Dark: a lighter hover on the raised menu surface so coloured (error/success) items keep 4.5:1.
          '&:hover': {
            backgroundColor: isDark ? 'rgba(238,242,232,0.05)' : undefined,
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '0.9375rem',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: isDark ? brandDark.raised : undefined,
          border: isDark ? `1px solid ${brandDark.line}` : undefined,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: isDark ? brandDark.raised : undefined,
          border: isDark ? `1px solid ${brandDark.line}` : undefined,
        },
      },
    },
    MuiTabs: {
      defaultProps: {
        variant: 'scrollable',
        scrollButtons: 'auto',
        allowScrollButtonsMobile: true,
      },
      styleOverrides: {
        root: {
          minHeight: 'auto',
        },
        scrollButtons: {
          '&.Mui-disabled': {
            opacity: 0.3,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: isDark ? brandDark.text2 : undefined,
          '&.Mui-selected': {
            color: isDark ? brand.paper : undefined,
          },
          // Ensure tabs don't shrink to unreadable sizes on mobile
          minWidth: 'auto',
          padding: '12px 16px',
          '@media (max-width: 600px)': {
            padding: '8px 12px',
            fontSize: '0.8125rem',
          },
        },
      },
    },
  };
}

function getMiscOverrides(isDark: boolean): Components<Omit<Theme, 'components'>> {
  return {
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontWeight: 500,
          fontSize: '0.875rem',
          // MUI fills default chips with action.selected (a green tint in dark); dark uses the top surface.
          // A variant (not a nested selector) so a chip's own sx background still wins.
          ...(isDark && {
            variants: [
              { props: { variant: 'filled', color: 'default' }, style: { backgroundColor: brandDark.top } },
              // Outlined chip labels take the lighter tone so they read on hovered/selected rows too.
              { props: { variant: 'outlined', color: 'primary' }, style: { color: brandDark.accentLight } },
              { props: { variant: 'outlined', color: 'success' }, style: { color: brandDark.accentLight } },
              { props: { variant: 'outlined', color: 'error' }, style: { color: brandDark.errorLight } },
              { props: { variant: 'outlined', color: 'warning' }, style: { color: '#f5c97e' } },
              { props: { variant: 'outlined', color: 'info' }, style: { color: brandDark.infoLight } },
            ],
          }),
        },
        outlined: {
          borderColor: isDark ? brandDark.outline : undefined,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        // Filled alerts: MUI sets ink on the colour in dark, but Typography inside bakes in the cream body colour.
        filled: {
          '& .MuiTypography-root': {
            color: isDark ? 'inherit' : undefined,
          },
        },
        standardSuccess: {
          backgroundColor: isDark ? alpha(brandDark.accent, 0.15) : brand.accentSoft,
          color: isDark ? brandDark.successText : brand.ink,
          '& .MuiAlert-icon': {
            color: isDark ? brandDark.accent : brand.accent,
          },
        },
        standardWarning: {
          backgroundColor: isDark ? alpha(brandDark.warning, 0.15) : '#FBF1DF',
          color: isDark ? brandDark.warningLight : brand.ink,
          '& .MuiAlert-icon': {
            color: isDark ? brandDark.warning : brand.warning,
          },
        },
        standardError: {
          backgroundColor: isDark ? alpha(brandDark.error, 0.15) : '#F7E2DF',
          color: isDark ? brandDark.errorLight : brand.ink,
          '& .MuiAlert-icon': {
            color: isDark ? brandDark.error : brand.error,
          },
        },
        standardInfo: {
          backgroundColor: isDark ? alpha(brandDark.info, 0.15) : alpha(brand.accent, 0.08),
          color: isDark ? brandDark.infoLight : brand.accentDeep,
          '& .MuiAlert-icon': {
            color: isDark ? brandDark.info : brand.accent,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: isDark ? brandDark.line : undefined,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: isDark ? brandDark.top : undefined,
          color: isDark ? brand.paper : undefined,
        },
        arrow: {
          color: isDark ? brandDark.top : undefined,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: isDark ? 'rgba(238,242,232,0.12)' : undefined,
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        // MUI's value label uses grey[600], which the inverted dark grey scale makes light (white text on it).
        valueLabel: {
          backgroundColor: isDark ? brandDark.top : undefined,
          color: isDark ? brand.paper : undefined,
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        // MUI emphasises background.default to near-white in dark; use the top surface instead.
        root: {
          backgroundColor: isDark ? brandDark.top : undefined,
          color: isDark ? brand.paper : undefined,
        },
        action: {
          color: isDark ? brandDark.accentLight : undefined,
        },
      },
    },
  };
}

export function getComponentOverrides(isDark: boolean): Components<Omit<Theme, 'components'>> {
  return {
    ...getLayoutOverrides(isDark),
    ...getButtonOverrides(isDark),
    ...getInputOverrides(isDark),
    ...getNavigationOverrides(isDark),
    ...getMiscOverrides(isDark),
  };
}
